/**
 * whatsappTemplates — fallback local catalog for BulkWhatsappModal when
 * the shared backend catalog (`message_templates`) is unreachable or
 * empty. Historically this was the primary source of truth for Bulk
 * bodies + operator overrides; PR #104 moved the primary path to the
 * backend. localStorage keys (built-in overrides, customs, last-editor)
 * are preserved untouched — they resurface only under the fallback
 * banner, clearly labelled as local so operators know they are not
 * shared with the team.
 *
 * The template body still carries variables like {nombre} and
 * {link_dossier}; rendering happens in
 * `bulkWhatsapp.renderWhatsappTemplate`.
 */

export const TEMPLATE_STORAGE_KEY = "dolce_whatsapp_custom_templates_v1";
export const LAST_EDITOR_STORAGE_KEY = "dolce_whatsapp_last_editor_v1";
export const MAX_CUSTOM_TEMPLATES = 20;
export const CUSTOM_PREFIX = "custom_";

const FIRST_CONTACT_BODY =
  "Hola {nombre}, soy del equipo de Dolce Villas Lombok.\n\n" +
  "Te escribo porque dejaste tus datos interesado en nuestras villas en Kuta Lombok.\n\n" +
  "¿Te gustaría que te envíe el dossier con toda la información?";

const SEND_DOSSIER_BODY =
  "Hola {nombre}, te comparto el dossier de Dolce Villas Lombok:\n\n" +
  "{link_dossier}\n\n" +
  "Cuando lo revises, podemos organizar una llamada para resolver cualquier duda.";

const FOLLOW_UP_NO_REPLY_BODY =
  "Hola {nombre}, quería saber si pudiste revisar la información de Dolce Villas Lombok.\n\n" +
  "¿Te interesa que organicemos una llamada breve?";

const CONFIRM_CALL_BODY =
  "Hola {nombre}, te confirmo nuestra llamada.\n\n" +
  "Si necesitas cambiar la hora, avísame por aquí.";

const REMIND_CALL_BODY =
  "Hola {nombre}, te recuerdo que tenemos una llamada agendada.\n\n" +
  "Nos vemos en breve.";

const FOLLOW_UP_AFTER_CALL_BODY =
  "Hola {nombre}, gracias por tu tiempo en la llamada.\n\n" +
  "Te escribo por si te ha quedado alguna duda sobre Dolce Villas Lombok.";

// Ordered so the select renders in the natural funnel sequence.
export const WHATSAPP_TEMPLATES = [
  { key: "first_contact",        label: "Primer contacto",              body: FIRST_CONTACT_BODY },
  { key: "send_dossier",         label: "Envío de dossier",             body: SEND_DOSSIER_BODY },
  { key: "follow_up_no_reply",   label: "Seguimiento sin respuesta",    body: FOLLOW_UP_NO_REPLY_BODY },
  { key: "confirm_call",         label: "Confirmar llamada",            body: CONFIRM_CALL_BODY },
  { key: "remind_call",          label: "Recordatorio de llamada",      body: REMIND_CALL_BODY },
  { key: "follow_up_after_call", label: "Seguimiento después de llamada", body: FOLLOW_UP_AFTER_CALL_BODY },
  { key: "custom",               label: "Mensaje personalizado",        body: "" },
];

export const DEFAULT_TEMPLATE_KEY = "first_contact";
const BUILT_IN_KEYS = new Set(WHATSAPP_TEMPLATES.map((t) => t.key));

/**
 * True when the key corresponds to a static template. Used to prevent the
 * UI from offering "Eliminar" on built-ins.
 */
export function isBuiltInTemplate(key) {
  return BUILT_IN_KEYS.has(key);
}

// ---------------------------------------------------------------------------
// Built-in template overrides — localStorage persistence
// ---------------------------------------------------------------------------
// Lets marketing edit the hardcoded built-in bodies without touching source.
// Stored separately from custom templates so the two persistence layers stay
// independent and a bad override never poisons the custom-templates blob.
// "custom" is not overridable — its body is always an empty seed.

export const BUILTIN_OVERRIDES_STORAGE_KEY = "dolce_whatsapp_builtin_template_overrides_v1";

/**
 * Read the persisted overrides map. Filters out unknown keys, "custom",
 * and non-string bodies so downstream code can trust the shape.
 */
export function loadBuiltInOverrides() {
  try {
    const raw = window.localStorage.getItem(BUILTIN_OVERRIDES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v !== "string") continue;
      if (!BUILT_IN_KEYS.has(k) || k === "custom") continue;
      out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function persistBuiltInOverrides(map) {
  try {
    window.localStorage.setItem(BUILTIN_OVERRIDES_STORAGE_KEY, JSON.stringify(map));
    return true;
  } catch {
    return false;
  }
}

/**
 * Store an override body for a built-in templateKey. Rejects "custom" and
 * unknown keys so callers can't accidentally poison the storage.
 */
export function saveBuiltInOverride(templateKey, body) {
  if (!BUILT_IN_KEYS.has(templateKey) || templateKey === "custom") return false;
  const current = loadBuiltInOverrides();
  current[templateKey] = String(body ?? "");
  return persistBuiltInOverrides(current);
}

/**
 * Remove the override for a built-in. Returns false when there was nothing
 * to remove so the UI can decide whether to warn.
 */
export function deleteBuiltInOverride(templateKey) {
  const current = loadBuiltInOverrides();
  if (!Object.prototype.hasOwnProperty.call(current, templateKey)) return false;
  delete current[templateKey];
  return persistBuiltInOverrides(current);
}

export function hasBuiltInOverride(templateKey) {
  const current = loadBuiltInOverrides();
  return Object.prototype.hasOwnProperty.call(current, templateKey);
}

/**
 * Effective body for any key: override for built-ins (except "custom"),
 * hardcoded original as fallback, or the persisted custom body when the
 * key belongs to a user-created template. Null when nothing matches.
 */
export function getEffectiveTemplateBody(templateKey) {
  if (BUILT_IN_KEYS.has(templateKey)) {
    if (templateKey !== "custom") {
      const overrides = loadBuiltInOverrides();
      if (Object.prototype.hasOwnProperty.call(overrides, templateKey)) {
        return overrides[templateKey];
      }
    }
    const builtin = WHATSAPP_TEMPLATES.find((t) => t.key === templateKey);
    return builtin ? builtin.body : null;
  }
  const custom = loadCustomTemplates().find((t) => t.key === templateKey);
  return custom ? custom.body : null;
}

// ---------------------------------------------------------------------------
// Custom templates — localStorage persistence
// ---------------------------------------------------------------------------

/**
 * Read the persisted custom templates. Returns [] on any parse/access error
 * so the modal never crashes because of a bad snapshot.
 */
export function loadCustomTemplates() {
  try {
    const raw = window.localStorage.getItem(TEMPLATE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t) => t && typeof t === "object"
        && typeof t.key === "string"
        && typeof t.label === "string"
        && typeof t.body === "string",
    );
  } catch {
    return [];
  }
}

function persistCustomTemplates(list) {
  try {
    window.localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

/**
 * Append a custom template. Returns the created record (with generated
 * `key`) or `null` if the cap was hit or persistence failed. The name is
 * trimmed and stored on `label`; empty names are rejected.
 */
export function saveCustomTemplate(name, body) {
  const cleanName = String(name || "").trim();
  if (!cleanName) return null;
  const existing = loadCustomTemplates();
  if (existing.length >= MAX_CUSTOM_TEMPLATES) return null;
  const entry = {
    key: `${CUSTOM_PREFIX}${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    label: cleanName.slice(0, 60),
    body: String(body ?? ""),
    createdAt: new Date().toISOString(),
  };
  const next = [...existing, entry];
  if (!persistCustomTemplates(next)) return null;
  return entry;
}

/**
 * Remove a custom template by key. Built-in keys are ignored (returning
 * false) so the UI can't accidentally wipe the standard set.
 */
export function deleteCustomTemplate(key) {
  if (!key || isBuiltInTemplate(key)) return false;
  const existing = loadCustomTemplates();
  const next = existing.filter((t) => t.key !== key);
  if (next.length === existing.length) return false;
  return persistCustomTemplates(next);
}

/**
 * Body lookup. Delegates to getEffectiveTemplateBody so built-in overrides
 * apply uniformly wherever the modal reads a template body (editor seed,
 * template-change reset, dirty-flag baseline, fallback on custom delete).
 */
export function getTemplateBody(key) {
  return getEffectiveTemplateBody(key);
}

/**
 * Merged list used to build the dropdown. Built-ins first (in canonical
 * order), then customs. Each entry has {key, label, body, isCustom,
 * hasOverride}. Built-in bodies reflect the effective body (override when
 * present) so downstream callers never render a stale hardcoded string.
 */
export function getAllTemplates() {
  const overrides = loadBuiltInOverrides();
  const builtins = WHATSAPP_TEMPLATES.map((t) => {
    const hasOverride = t.key !== "custom"
      && Object.prototype.hasOwnProperty.call(overrides, t.key);
    return {
      ...t,
      body: hasOverride ? overrides[t.key] : t.body,
      isCustom: false,
      hasOverride,
    };
  });
  const customs = loadCustomTemplates().map((t) => ({
    ...t,
    isCustom: true,
    hasOverride: false,
  }));
  return [...builtins, ...customs];
}

// ---------------------------------------------------------------------------
// Last editor — remembers the operator's most recent template + body across
// modal reopens even when the queue snapshot has expired or been cleared.
// Deliberately independent from the queue lifecycle so allDone doesn't wipe
// what the operator typed.
// ---------------------------------------------------------------------------

export function loadLastEditor() {
  try {
    const raw = window.localStorage.getItem(LAST_EDITOR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.templateKey !== "string") return null;
    if (typeof parsed.template !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveLastEditor(templateKey, template) {
  try {
    window.localStorage.setItem(
      LAST_EDITOR_STORAGE_KEY,
      JSON.stringify({
        templateKey,
        template,
        updatedAt: new Date().toISOString(),
      }),
    );
    return true;
  } catch {
    return false;
  }
}
